import { useEffect, useMemo, useState } from "react";
import api, { apiErr, runJob } from "@/lib/api";
import { ui } from "@/lib/ui";
import { toast } from "sonner";
import {
  Truck, HardHat, Store, MapPin, Camera, Images, Download, Trash2,
  Loader2, Wand2, Upload, Sparkles, History,
} from "lucide-react";

const TYPES = [
  { key: "truck", label: "Company Truck", icon: Truck, desc: "Branded service vehicle in a real-world setting." },
  { key: "onsite", label: "On-Site / Job Photo", icon: HardHat, desc: "Technicians working a job, matched to your trade." },
  { key: "storefront", label: "Storefront / Building", icon: Store, desc: "Your branding on a realistic storefront." },
  { key: "exterior", label: "Business Exterior", icon: MapPin, desc: "Street-level exterior of your business." },
  { key: "custom", label: "Custom Business Photo", icon: Camera, desc: "Describe any marketing photo you need." },
];
const VEHICLES = ["pickup truck", "service van", "cargo van", "box truck", "work truck", "flatbed truck", "trailer"];
const VEHICLE_COLORS = ["white", "black", "silver", "gray", "navy blue", "royal blue", "red", "dark green", "orange", "yellow"];
const SCENES = [
  { v: "residential", l: "Outside a residential home" },
  { v: "jobsite", l: "Arriving at a job site" },
  { v: "commercial", l: "Commercial parking lot" },
  { v: "street", l: "On a normal street" },
];
const TIMES = ["morning", "midday", "late afternoon", "overcast day", "golden hour", "evening"];
const WEATHER = ["clear", "partly cloudy", "overcast", "after light rain"];

const emptyBiz = { business_name: "", address: "", phone: "", category: "", website: "", service_description: "", logo_url: "", logo_id: "" };

export default function ImageGenerator() {
  const [tab, setTab] = useState("create");
  const [profiles, setProfiles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [profileId, setProfileId] = useState("");
  const [biz, setBiz] = useState(emptyBiz);
  const [saveProfile, setSaveProfile] = useState(true);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoGen, setLogoGen] = useState(false);
  const [selected, setSelected] = useState({ truck: true, onsite: true });
  const [config, setConfig] = useState({});
  const [perType, setPerType] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [results, setResults] = useState([]);
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(true);

  const loadProfiles = () => api.get("/images/profiles").then(({ data }) => setProfiles(data)).catch(() => {});
  const loadHistory = () => api.get("/images/history").then(({ data }) => setHistory(data)).catch(() => {}).finally(() => setHistLoading(false));
  useEffect(() => {
    loadProfiles();
    loadHistory();
    api.get("/images/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  const setB = (k, v) => setBiz((b) => ({ ...b, [k]: v }));
  const setC = (k, v) => setConfig((c) => ({ ...c, [k]: v }));
  const toggleType = (k) => setSelected((s) => ({ ...s, [k]: !s[k] }));
  const chosenTypes = useMemo(() => TYPES.filter((t) => selected[t.key]).map((t) => t.key), [selected]);

  const pickExisting = (id) => {
    setProfileId(id);
    const p = profiles.find((x) => x.id === id);
    setBiz(p ? { ...emptyBiz, ...p } : emptyBiz);
  };

  const genLogo = async () => {
    if (!biz.business_name.trim() || !biz.category) return toast.error("Enter a business name and pick a category first");
    setLogoGen(true);
    try {
      const data = await runJob("/logo/preview", { business_name: biz.business_name, category: biz.category });
      setBiz((b) => ({ ...b, logo_id: data.logo_id, logo_url: data.logo_url }));
      toast.success("Logo generated");
    } catch (err) { toast.error(apiErr(err)); } finally { setLogoGen(false); }
  };

  const onLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLogoBusy(true);
    const r = new FileReader();
    r.onload = async () => {
      try {
        const { data } = await api.post("/images/logo", { image_base64: String(r.result) });
        setBiz((b) => ({ ...b, logo_id: data.logo_id, logo_url: data.logo_url }));
        toast.success("Logo uploaded");
      } catch (err) { toast.error(apiErr(err)); }
      finally { setLogoBusy(false); }
    };
    r.readAsDataURL(f);
    e.target.value = "";
  };

  const generate = async () => {
    if (!chosenTypes.length) { toast.error("Pick at least one photo type"); return; }
    if (!biz.business_name.trim()) { toast.error("Business name is required"); return; }
    setGenerating(true);
    try {
      let pid = profileId;
      if (!pid && saveProfile) {
        try { const { data } = await api.post("/images/profiles", biz); pid = data.id; setProfileId(pid); loadProfiles(); } catch { /* non-fatal */ }
      }
      const payload = { types: chosenTypes, config, variants_per_type: Number(perType) };
      if (pid) payload.profile_id = pid; else payload.business = biz;
      const data = await runJob("/images/generate-batch", payload);
      setResults(data.images);
      loadHistory();
      toast.success(`Generated ${data.images.length} image(s)`);
    } catch (err) { toast.error(apiErr(err)); }
    finally { setGenerating(false); }
  };

  const moreVariants = async (id) => {
    toast.message("Generating more variants…");
    try {
      const data = await runJob(`/images/${id}/variants`, { count: 2 });
      setResults((r) => [...data.images, ...r]);
      loadHistory();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const del = async (id, where) => {
    try {
      await api.delete(`/images/${id}`);
      if (where === "results") setResults((r) => r.filter((x) => x.id !== id));
      setHistory((h) => h.filter((x) => x.id !== id));
    } catch (err) { toast.error(apiErr(err)); }
  };

  const typeLabel = (k) => TYPES.find((t) => t.key === k)?.label || k;

  const Card = ({ img, where }) => (
    <div className={`${ui.card} overflow-hidden group`} data-testid={`gen-image-${img.id}`}>
      <div className="aspect-[3/2] bg-slate-950 overflow-hidden">
        <img src={img.url} alt={img.image_type} className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="flex items-center justify-between p-3 gap-2">
        <span className="text-[11px] font-mono uppercase tracking-wider text-slate-400">{typeLabel(img.image_type)}</span>
        <div className="flex items-center gap-1">
          <a href={img.url} target="_blank" rel="noreferrer" download data-testid={`download-${img.id}`}
            className="p-1.5 text-slate-400 hover:text-indigo-300" title="Download"><Download className="w-4 h-4" /></a>
          {where === "results" && (
            <button onClick={() => moreVariants(img.id)} className="p-1.5 text-slate-400 hover:text-emerald-300" title="More variants"><Sparkles className="w-4 h-4" /></button>
          )}
          <button onClick={() => del(img.id, where)} data-testid={`delete-image-${img.id}`} className="p-1.5 text-slate-400 hover:text-rose-400" title="Delete"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="animate-fade-up" data-testid="image-generator-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className={ui.pageHeader}>Image Generation</h1>
          <p className={`${ui.muted} mt-1`}>Create realistic, on-brand marketing photos — pick several at once.</p>
        </div>
        <div className="flex gap-1 bg-slate-900/60 border border-slate-800 rounded-lg p-1">
          <button onClick={() => setTab("create")} data-testid="tab-create" className={`px-3 py-1.5 rounded-md text-sm font-medium ${tab === "create" ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-slate-200"}`}>Create</button>
          <button onClick={() => { setTab("history"); loadHistory(); }} data-testid="tab-history" className={`px-3 py-1.5 rounded-md text-sm font-medium ${tab === "history" ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-slate-200"}`}>History</button>
        </div>
      </div>

      {tab === "history" && (
        <div data-testid="history-panel">
          {histLoading ? <div className="text-slate-500 py-10 text-center"><Loader2 className="w-5 h-5 spin inline" /></div> :
            history.length === 0 ? <div className={`${ui.card} p-10 text-center text-slate-500`}><History className="w-6 h-6 mx-auto mb-2 opacity-50" />No images yet. Create your first one.</div> :
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{history.map((img) => <Card key={img.id} img={img} where="history" />)}</div>}
        </div>
      )}

      {tab === "create" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-4">
            {/* Business info */}
            <div className={`${ui.card} p-5`}>
              <h2 className={`${ui.cardTitle} mb-4`}>Business information</h2>
              {profiles.length > 0 && (
                <div className="mb-4">
                  <label className={ui.label}>Use existing business</label>
                  <select data-testid="use-existing-business" className={ui.input} value={profileId} onChange={(e) => pickExisting(e.target.value)}>
                    <option value="">— New business —</option>
                    {profiles.map((p) => <option key={p.id} value={p.id}>{p.business_name}{p.category ? ` · ${p.category}` : ""}</option>)}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className={ui.label}>Business name *</label><input data-testid="biz-name" className={ui.input} value={biz.business_name} onChange={(e) => setB("business_name", e.target.value)} /></div>
                <div>
                  <label className={ui.label}>Category / Service</label>
                  <select data-testid="biz-category" className={ui.input} value={biz.category} onChange={(e) => setB("category", e.target.value)}>
                    <option value="">Select category…</option>
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                    {biz.category && !categories.includes(biz.category) && <option value={biz.category}>{biz.category}</option>}
                  </select>
                </div>
                <div><label className={ui.label}>Phone</label><input data-testid="biz-phone" className={ui.input} value={biz.phone} onChange={(e) => setB("phone", e.target.value)} /></div>
                <div><label className={ui.label}>Address</label><input data-testid="biz-address" className={ui.input} value={biz.address} onChange={(e) => setB("address", e.target.value)} /></div>
                <div><label className={ui.label}>Website (optional)</label><input data-testid="biz-website" className={ui.input} value={biz.website} onChange={(e) => setB("website", e.target.value)} /></div>
                <div>
                  <label className={ui.label}>Company logo</label>
                  <div className="flex items-center gap-3">
                    <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 bg-slate-800 rounded-lg text-sm text-slate-200 hover:bg-slate-700">
                      <input type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="logo-upload" />
                      {logoBusy ? <Loader2 className="w-4 h-4 spin" /> : <Upload className="w-4 h-4" />} Upload
                    </label>
                    <button type="button" onClick={genLogo} disabled={logoGen} data-testid="logo-generate"
                      className="inline-flex items-center gap-2 px-3 py-2 bg-slate-800 rounded-lg text-sm text-slate-200 hover:bg-slate-700 disabled:opacity-60">
                      {logoGen ? <Loader2 className="w-4 h-4 spin" /> : <Wand2 className="w-4 h-4 text-indigo-400" />} {logoGen ? "Generating…" : "Generate"}
                    </button>
                    {biz.logo_url && <img src={biz.logo_url} alt="logo" className="h-8 bg-white rounded px-1" data-testid="logo-preview" />}
                  </div>
                </div>
              </div>
            </div>

            {/* Photo types */}
            <div className={`${ui.card} p-5`}>
              <h2 className={`${ui.cardTitle} mb-1`}>Which photos to generate</h2>
              <p className={`${ui.muted} mb-4`}>Tick one or more — they'll all be generated in one go.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="type-checkboxes">
                {TYPES.map((t) => (
                  <label key={t.key} data-testid={`type-check-${t.key}`}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selected[t.key] ? "border-indigo-500/60 bg-indigo-500/10" : "border-slate-800 hover:border-slate-700"}`}>
                    <input type="checkbox" className="mt-1" checked={!!selected[t.key]} onChange={() => toggleType(t.key)} />
                    <div>
                      <div className="flex items-center gap-2 text-slate-100 text-sm font-medium"><t.icon className="w-4 h-4 text-indigo-400" />{t.label}</div>
                      <p className="text-xs text-slate-500 mt-0.5">{t.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Truck options */}
            {selected.truck && (
              <div className={`${ui.card} p-5 grid grid-cols-1 sm:grid-cols-2 gap-4`} data-testid="truck-config">
                <div className="sm:col-span-2 text-sm font-semibold text-slate-300 flex items-center gap-2"><Truck className="w-4 h-4 text-indigo-400" /> Truck options</div>
                <div><label className={ui.label}>Vehicle type</label><select className={ui.input} value={config.vehicle_type || ""} onChange={(e) => setC("vehicle_type", e.target.value)}><option value="">Service van</option>{VEHICLES.map((v) => <option key={v} value={v}>{v}</option>)}</select></div>
                <div><label className={ui.label}>Vehicle color</label><select data-testid="vehicle-color" className={ui.input} value={config.vehicle_color || ""} onChange={(e) => setC("vehicle_color", e.target.value)}><option value="">Auto</option>{VEHICLE_COLORS.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
                <div><label className={ui.label}>Scene</label><select className={ui.input} value={config.scene || ""} onChange={(e) => setC("scene", e.target.value)}><option value="">Outside a home</option>{SCENES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}</select></div>
                <div><label className={ui.label}>Time of day</label><select className={ui.input} value={config.time_of_day || ""} onChange={(e) => setC("time_of_day", e.target.value)}><option value="">Auto</option>{TIMES.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
                <div><label className={ui.label}>Weather</label><select className={ui.input} value={config.weather || ""} onChange={(e) => setC("weather", e.target.value)}><option value="">Auto</option>{WEATHER.map((w) => <option key={w} value={w}>{w}</option>)}</select></div>
              </div>
            )}

            <div className={`${ui.card} p-5`}>
              <label className={ui.label}>Additional instructions (optional)</label>
              <textarea className={ui.textarea} value={config.additional_instructions || ""} onChange={(e) => setC("additional_instructions", e.target.value)} placeholder="Anything specific you'd like in the photos…" />
            </div>
          </div>

          {/* Summary / generate */}
          <div className="space-y-4">
            <div className={`${ui.card} p-5 lg:sticky lg:top-4`} data-testid="confirm-summary">
              <h2 className={`${ui.cardTitle} mb-3`}>Summary</h2>
              <div className="grid grid-cols-2 gap-y-1.5 text-sm text-slate-400">
                <span>Business</span><span className="text-slate-200 text-right truncate">{biz.business_name || "—"}</span>
                <span>Category</span><span className="text-slate-200 text-right truncate">{biz.category || "—"}</span>
                <span>Phone</span><span className="text-slate-200 text-right truncate">{biz.phone || "—"}</span>
                <span>Logo</span><span className="text-right">{biz.logo_url ? <span className="text-emerald-400">Uploaded ✓</span> : <span className="text-slate-500">none</span>}</span>
                <span>Photos</span><span className="text-slate-200 text-right">{chosenTypes.length} type(s)</span>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <label className="text-sm text-slate-300">Per type</label>
                <select data-testid="per-type-count" className={`${ui.input} w-20 py-1.5`} value={perType} onChange={(e) => setPerType(e.target.value)}>{[1, 2, 3].map((n) => <option key={n} value={n}>{n}</option>)}</select>
                <span className="text-xs text-slate-500">= {chosenTypes.length * perType} image(s)</span>
              </div>
              {!profileId && (
                <label className="flex items-center gap-2 text-sm text-slate-400 mt-3"><input type="checkbox" checked={saveProfile} onChange={(e) => setSaveProfile(e.target.checked)} /> Save business for reuse</label>
              )}
              <button data-testid="generate-btn" onClick={generate} disabled={generating} className={`${ui.btnPrimary} w-full mt-4 justify-center`}>
                {generating ? <><Loader2 className="w-4 h-4 spin" /> Generating…</> : <><Wand2 className="w-4 h-4" /> Generate Images</>}
              </button>
              {generating && <p className="text-xs text-slate-500 mt-2 text-center">Realistic generation can take up to a minute.</p>}
            </div>
          </div>

          {results.length > 0 && (
            <div className="lg:col-span-3" data-testid="results-panel">
              <div className="flex items-center gap-2 mb-3"><Images className="w-4 h-4 text-indigo-400" /><h2 className={ui.sectionHeader}>Results</h2></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{results.map((img) => <Card key={img.id} img={img} where="results" />)}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
